import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getImageMime } from 'utils';
import ButtonsBlockRow from 'components/ButtonsBlockRow';
import { object, string } from 'yup';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Infobox from '../../ui-library/components/Infobox';
import {
    Column, ContentBox, Title, Row, Modal, FileInput, Button, TextInput, notification,
} from '../../ui-library';
import { useManagerProfile } from '../../prodivers/managerProfile';
import PageSection from '../../components/PageSection';
import Preloader from '../../components/Preloader';
import { adaptPictureFile } from './adaptors/adaptPictureFile';
import { memberIdSelector } from '../../redux-store/auth/authSelectors';
import { useChangeProfilePicture } from './hooks/useChangeProfilePicture';
import './EditProfile.css';
import Avatar from '../../ui-library/components/Avatar';
import { useYupValidationResolver } from '../../hooks/useYupValidationResolver';
import FieldControl from '../../components/FieldControl/FieldControl';
import ServiceManager from '../../services/ServiceManager';

type UploadParams = {
    ImageContentBase64?: string;
    size?: number;
    extension?: string;
} | null;

type FormData = {
    phoneNumber: string;
    address: string;
    city: string;
    zip: string;
};

const EditProfile = () => {
    const {
        data, isLoading, error, getMemberInfo, memberInfo, getMemberProfilePicture,
    } = useManagerProfile();
    const { t } = useTranslation();
    const memberId = useSelector(memberIdSelector) as string;
    const navigate = useNavigate();

    const [uploadParams, setUploadParams] = useState<UploadParams>(null);
    const [uploadError, setUploadError] = useState<unknown>(null);
    const [sizeError, setSizeError] = useState(false);
    const [extensionError, setExtensionError] = useState(false);

    const onPictureChange = useCallback(async (files: File[]) => {
        if (files.length === 0) {
            setUploadParams(null);

            return;
        }
        try {
            const fileParams = await adaptPictureFile(files);

            setUploadParams(fileParams);
            setSizeError((fileParams.size || 0) >= 2097152);
            setExtensionError(!['jpg', 'png', 'jpeg'].includes(fileParams.extension?.toLowerCase() || ''));
        } catch (err) {
            setUploadError(err);
        }
    }, []);

    const schema = useMemo(() => object().shape({
        phoneNumber: string().matches(/^[+][(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,7}$/, t('onboardingFlow.personalPage.validationPhoneNumber')).required(t('validation.mandatoryField')),
        address: string().required(t('validation.mandatoryField')),
        city: string().required(t('validation.mandatoryField')),
        zip: string().required(t('validation.mandatoryField')),
    }), [t]);

    const resolver = useYupValidationResolver(schema);

    const {
        register, handleSubmit, formState: { errors }, setValue, watch, reset,
    } = useForm<FormData>({
        resolver,
        defaultValues: {
            phoneNumber: '',
            address: '',
            city: '',
            zip: '',
        },
    });

    useEffect(() => {
        reset({
            phoneNumber: (memberInfo as { phoneNumber?: string })?.phoneNumber,
            address: (memberInfo as { streetAddress?: string })?.streetAddress,
            city: (memberInfo as { city?: string })?.city,
            zip: (memberInfo as { postalCode?: string })?.postalCode,
        });
    }, [memberInfo, reset]);

    const {
        phoneNumber, address, city, zip,
    } = watch();

    const profilePicture = useMemo(
        () => {
            const currentImage = (data as { hasProfilePicture?: boolean; image?: string })?.hasProfilePicture ? (data as { image?: string })?.image : null;

            if (uploadParams?.ImageContentBase64 == null) {
                return currentImage;
            }

            if (uploadParams?.ImageContentBase64 === '') {
                return '';
            }

            const newImage = `data:${getImageMime(uploadParams?.ImageContentBase64)};base64,${uploadParams?.ImageContentBase64}`;

            return newImage ?? currentImage;
        },
        [data, uploadParams?.ImageContentBase64],
    );

    const {
        changeMemberProfilePicture,
        error: errorChangeProfilePicture,
        isLoading: isLoadingChangeProfilePicture,
        deleteMemberProfilePicture,
    } = useChangeProfilePicture();

    const onSave = handleSubmit(async (formData) => {
        if (sizeError || extensionError) {
            return;
        }

        Modal.confirm({
            title: t('editProfile.saveChangesTitle'),
            content: t('editProfile.saveChangesTitleContent'),
            okText: t('editProfile.yes'),
            cancelText: t('editProfile.no'),
            onOk: async () => {
                await ServiceManager.customMembersService('saveMemberInfo', [{
                    memberId,
                    streetAddress: formData.address,
                    postalCode: formData.zip,
                    city: formData.city,
                    phoneNumber: formData.phoneNumber,
                }]);
                if (uploadParams?.ImageContentBase64 !== undefined) {
                    if ((uploadParams?.ImageContentBase64 || '').length === 0) {
                        await deleteMemberProfilePicture(memberId);
                    } else {
                        await changeMemberProfilePicture(
                            memberId,
                            { ImageContentBase64: uploadParams.ImageContentBase64 },
                        );
                    }
                    await getMemberProfilePicture();
                }
                await getMemberInfo();
                notification.open({ content: t('editProfile.successfullySaved'), type: 'success' });
            },
        });
    });

    const onPhoneNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const phoneValue = e.target?.value;

        if (phoneValue && !phoneValue.startsWith('+')) {
            setValue('phoneNumber', `+${phoneValue}`);
        } else {
            setValue('phoneNumber', phoneValue);
        }
    }, [setValue]);

    return (
        <PageSection>
            <Preloader
                isLoading={isLoading}
                error={error || uploadError || errorChangeProfilePicture}
            >
                <ContentBox className="edit-profile">
                    <Title type={2} className="underlined with-mb">{t('editProfile.title')}</Title>
                    <Row>
                        <Column size={['lg-2', 'md-3', 'sm-12']}>
                            <Avatar
                                name={(data as { firstName?: string })?.firstName}
                                src={(profilePicture) as string}
                                size="large"
                            />
                            <div className="upload-buttons">
                                <FileInput
                                    accept=".jpg, .png, .jpeg"
                                    uploadText={t('editProfile.upload')}
                                    onChange={onPictureChange}
                                    buttonType="primary"
                                    showFileList={false}
                                />
                                <Button
                                    type="danger"
                                    size="small"
                                    onClick={() => setUploadParams({ ImageContentBase64: '' })}
                                >
                                    {t('editProfile.delete')}
                                </Button>
                            </div>
                        </Column>
                        <Column className="form-fields" size={['lg-5', 'md-6', 'sm-12']}>
                            <FieldControl
                                label={t('editProfile.fullName')}
                                value={(data as { name?: string })?.name}
                            />
                            <FieldControl
                                label={t('editProfile.email')}
                                value={(memberInfo as { email?: string })?.email}
                            />
                            <FieldControl
                                label={t('editProfile.role')}
                                value={(memberInfo as { role?: string })?.role}
                            />
                            <FieldControl
                                label={t('editProfile.personalNo')}
                                value={(memberInfo as { description?: string })?.description || '-'}
                            />
                            <TextInput
                                {...register('phoneNumber')}
                                name="phoneNumber"
                                label={t('editProfile.phone')}
                                type="tel"
                                value={phoneNumber}
                                onChange={onPhoneNumberChange}
                                error={errors.phoneNumber?.message}
                            />
                            <TextInput
                                {...register('address')}
                                name="address"
                                label={t('editProfile.address')}
                                value={address}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('address', e.target?.value)}
                                error={errors.address?.message}
                            />
                            <TextInput
                                {...register('zip')}
                                name="zip"
                                label={t('editProfile.zip')}
                                value={zip}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('zip', e.target?.value)}
                                error={errors.zip?.message}
                            />
                            <TextInput
                                {...register('city')}
                                name="city"
                                label={t('editProfile.city')}
                                value={city}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('city', e.target?.value)}
                                error={errors.city?.message}
                            />
                        </Column>
                        <Column size={['sm-12']}>
                            {sizeError && <Infobox error>{t('editProfile.profilePicture.sizeError')}</Infobox>}
                            {extensionError && <Infobox error>{t('editProfile.profilePicture.extensionError')}</Infobox>}
                        </Column>
                    </Row>
                    <ButtonsBlockRow
                        additionalButton={{
                            text: t('clientDetails.cancel'),
                            onClick: () => { navigate(-1); },
                        }}
                        primaryButton={{
                            text: t('clientDetails.save'),
                            loading: isLoadingChangeProfilePicture,
                            onClick: onSave,
                        }}
                    />
                </ContentBox>
            </Preloader>
        </PageSection>
    );
};

export default EditProfile;
